import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Login.css';
import logo from './assets/dg-logo.png';
import bwlogo from './assets/bw-logo.png';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [showRegister, setShowRegister] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    verificationNumber: '',
  });
  const [errors, setErrors] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    verificationNumber: '',
  });

  const [loginData, setLoginData] = useState({
    username: '',
    password: ''
  });

  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [registrationMessage, setRegistrationMessage] = useState('');
  const [verificationMessage, setVerificationMessage] = useState('');
  const [isVerificationRequired, setIsVerificationRequired] = useState(false);
  const [ssoMessage, setSsoMessage] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);

  const handleUserLogin = async () => {
    if (!validateEmail(loginData.username) || loginData.password === '') {
      setLoginError('Please enter valid login credentials.');
      return;
    }
  
    setIsLoggingIn(true);
    setLoginError('');
  
    try {
      const response = await fetch(
        'https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=login_user',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: loginData.username,
            password: loginData.password
          })
        }
      );
  
      const result = await response.json();
  
      if (!response.ok) {
        setLoginError(result.message || 'Login failed.');
        setIsLoggingIn(false);
        return;
      }
      localStorage.setItem("userToken", JSON.stringify(result));
      navigate('/manning');
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('Something went wrong. Please try again.');
      setIsLoggingIn(false);
    }
  };
    
  const handleRegister = () => {
    setShowRegister(true);
  };

  const handleCloseRegister = () => {
    setShowRegister(false);
    setFormData({ username: '', password: '', confirmPassword: '',verificationNumber: '' });
    setErrors({ username: '', password: '', confirmPassword: '', verificationNumber: '' });
  };

  useEffect(() => {
    const token = localStorage.getItem("userToken");

    if (!token) return;

    try {
      const parsed = JSON.parse(token);

      // OPTIONAL: check expiry if your token has one
      if (parsed?.expires_at && Date.now() > parsed.expires_at) {
        localStorage.removeItem("userToken");
        return;
      }

      navigate("/manning");
    } catch {
      localStorage.removeItem("userToken");
    }
  }, [navigate]);

  const validateEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const handleRegisterSubmit = async () => {
  const newErrors = { username: '', password: '', confirmPassword: '', verificationNumber: '' };

  if (!validateEmail(formData.username)) {
    newErrors.username = 'Invalid email address';
  }

  if (formData.password.length < 6) {
    newErrors.password = 'Password must be at least 6 characters';
  }

  if (formData.password !== formData.confirmPassword) {
    newErrors.confirmPassword = 'Passwords do not match';
  }

  setErrors(newErrors);

  const hasErrors = Object.values(newErrors).some((error) => error !== '');
  if (hasErrors) return;

  setIsRegistering(true);
  setRegistrationMessage('');

  try {
    const response = await fetch(
      'https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=register_user',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: formData.username,
          password: formData.password,
        }),
      }
    );

    const result = await response.json();

    if (!response.ok) {
      setRegistrationMessage(`Error: ${result.message}`);
      setIsRegistering(false);
      return;
    }

    setRegistrationMessage(
      'Registration successful! Please check your email for a verification code. Enter the code in the field below to complete your registration and gain access to the Staffing Portal. If you did not receive the email in the next 5 minutes, please check your spam or graymail folder, or contact the administrator for assistance.'
    );

    setIsVerificationRequired(true);
  } catch (error) {
    console.error('Registration error:', error);
    setRegistrationMessage('There was an error registering. Please try again.');
  } finally {
    setIsRegistering(false);
  }
};

  
  const handleVerify = async () => {
    try {
      const response = await fetch(
        'https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=verify_mfa',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            username:formData.username,
            password:formData.password,
            mfa_number: formData.verificationNumber,
          }),
        }
      );
  
      const result = await response.json();
  
      if (!response.ok) {
        setVerificationMessage(`Error: ${result.message}`);
        return;
      }
  
      setVerificationMessage('Your new account is verified. You can now login to the Staffing Portal.');
    } catch (error) {
      console.error('Verification error:', error);
      setVerificationMessage('Error: Verification failed. Please try again.');
    }
  };
    
  const handleChange1 = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: name === "username" ? value.toLowerCase() : value
    });
  };

  useEffect(() => {
    const token = localStorage.getItem("userToken");
    if (token) {
      navigate("/manning");
    }
  }, [navigate]);


  return (
    <div className="login-container">
      {/* Top Banner */}
      <div className="top-banner">
        <img src={bwlogo} alt="Design Group Logo" />
      </div>

      {/* Login Box */}
      <div className="login-box">
        <img src={logo} alt="Design Group Logo" className="login-logo" />
        <h1 className="login-title">Design Group Staffing Portal</h1>

        <div className="form-group">
          {loginError && <div className="error login-error">{loginError}</div>}
          <label>Username</label>
          <input
            type="email"
            placeholder="Enter email"
            name="username"
            value={loginData.username}
            onChange={(e) =>
              setLoginData({ ...loginData, [e.target.name]: e.target.value.toLowerCase() })
            }
          />
        </div>

        <div className="form-group">
          <label>Password</label>
          <input
            type="password"
            placeholder="Enter password"
            name="password"
            value={loginData.password}
            onChange={(e) =>
              setLoginData({ ...loginData, [e.target.name]: e.target.value })
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleUserLogin();
            }}
          />
        </div>

        <div className="login-center-button">
          <button className="main-login-button" onClick={handleUserLogin} disabled={isLoggingIn}>
            {isLoggingIn ? 'Logging in' : 'Login'}
          </button>
        </div>

        <div className="bottom-buttons">
          <button className="sso-button" onClick={() => setSsoMessage('Login using SSO: Coming soon')}>
            Login by BWDG SSO 
          </button>
          <button className="register-button" onClick={handleRegister}>Register</button>
        </div>

        { ssoMessage && <div className="info sso-message">{ssoMessage}</div> }
      </div>


      {/* Register Modal */}
      {showRegister && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Register</h2>
            {/* Display Registration or Verification Message */}
            {(verificationMessage || registrationMessage) && (
              <div className={`info ${verificationMessage ? (verificationMessage.startsWith('Error:') ? 'error' : 'success') : (registrationMessage.startsWith('Error:') ? 'error' : 'success')}`}>
                {verificationMessage || registrationMessage}
              </div>
            )}
            {/* If Verification is Successful, Only Show Success Message */}
            {verificationMessage === 'Your new account is verified. You can now login to the Staffing Portal.' ? (
              <div className="modal-actions">
                <button className="save-button" onClick={handleCloseRegister}>
                  Close
                </button>
              </div>
            ) : (
              !isVerificationRequired ? (
                <>
                  <div className="form-group">
                    <label>Username (Email)</label>
                    <input
                      type="email"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                    />
                    {errors.username && <div className="error">{errors.username}</div>}
                  </div>
                  <div className="form-group">
                    <label>Password</label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                    />
                    {errors.password && <div className="error">{errors.password}</div>}
                  </div>
                  <div className="form-group">
                    <label>Confirm Password</label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                    />
                    {errors.confirmPassword && (
                      <div className="error">{errors.confirmPassword}</div>
                    )}
                  </div>
                  <div className="modal-actions">
                    <button
                      className="save-button"
                      onClick={handleRegisterSubmit}
                      disabled={isRegistering}
                    >
                      {isRegistering ? 'Registering...' : 'Submit'}
                    </button>

                    <button className="save-button" onClick={handleCloseRegister}>
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label>Username (Email)</label>
                    <input
                      type="email"
                      name="username"
                      value={formData.username}
                      readOnly
                    />
                  </div>
                  <div className="form-group">
                    <label>Password</label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                    />
                    {errors.password && <div className="error">{errors.password}</div>}
                  </div>
                  <div className="form-group">
                    <label>Verification Number</label>
                    <input
                      type="text"
                      name="verificationNumber"
                      value={formData.verificationNumber}
                      onChange={handleChange}
                    />
                    {errors.verificationNumber && (
                      <div className="error">{errors.verificationNumber}</div>
                    )}
                  </div>
                  <div className="modal-actions">
                    <button className="save-button" onClick={handleVerify}>
                      Verify
                    </button>
                  </div>
                </>
              )
            )}
          </div>
        </div>
      )}


      {/* Bottom Banner */}
      <div className="bottom-banner"></div>
    </div>
  );
};

export default Login;
